import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Row } from 'react-bootstrap';
import useViewport from 'utils/useViewportHelper';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import Spinner from 'components/Spinner';
import useReferrerParam from 'hooks/useReferrerParam';
import { useNavigate } from 'react-router-dom';
import { RENEWAL } from 'constants/routeConstants';
import clsx from 'clsx';
import { PaymentError } from 'types/expiryRenewal';
import { Store } from 'types/store';
import InfoBlock from './common-components/InfoBlock/InfoBlock';
import useFetchPaymentData from './hooks/useFetchPaymentData';
import DirectDebitForm from './components/organisms/DirectDebitForm';
import CardPaymentForm from './components/organisms/CardPaymentForm';
import PaymentHeader from './components/molecules/PaymentHeader';
import PaymentPlan from './components/molecules/PaymentPlan';
import TimeoutWarningModal from './components/molecules/TimeoutWarningModal';
import Banner from './components/molecules/Banner';
import Stepper from './components/organisms/Stepper';

import './expiryRenewalGlobals.scss';

const CombinedPayments = () => {
  const [error, setError] = useState<PaymentError | null>(null);
  const [directDebitDataValid, setDirectDebitDataValid] = useState(false);
  const [directDebitEdit, setDirectDebitEdit] = useState(true);
  const { pageModel, paymentData } = useSelector((state: Store) => state.expiryRenewal);

  const { loading } = useFetchPaymentData({ setErrorModal: setError });
  const navigate = useNavigate();
  const isAppView = useReferrerParam();
  const { isMobile } = useViewport();
  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  const monthlyFlexDirectDebitPrice = paymentData?.basketContent?.remainingInstalmentPrice;
  const monthlyFlexCardPaymentPrice = paymentData?.basketContent?.depositPrice;

  useEffect(() => {
    cosmosTrackEvent('renewal_journey_combined_payment');
  }, []);

  useEffect(() => {
    if (!pageModel) navigate(RENEWAL, { replace: true });
  }, [pageModel, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center bg-grey">
        {isMobile ? <Spinner mobileSpinner /> : <Spinner bootSpinner />}
      </div>
    );
  }

  return (
    <>
      {pageModel?.discountModel?.isDiscountedPrice && (
        <Banner />
      )}
      <Stepper step={4} />
      <div className={clsx('fs-6 text-secondary', isAppView ? 'm-3 p-3 bg-white border rounded' : 'p-4')}>
        <PaymentHeader
          smallHeader="Payment 1"
          mainHeader="Set up your direct debit"
          description="We’ll need to take two payments to secure your cover. As we can’t always split the cost evenly, your direct debit and first payment may differ."
          price={monthlyFlexDirectDebitPrice}
        />
        {paymentData?.paymentPlan && (
          <PaymentPlan data={paymentData?.paymentPlan} />
        )}
        {directDebitEdit && (
          <Card className="border border-light shadow-sm bg-body rounded">
            <Row>
              <Col>
                {error?.message && error?.title ? (
                  <InfoBlock header={error?.title} variant="danger" showIcon>
                    <span className="fw-bold text-danger">
                      {error?.message}
                    </span>
                  </InfoBlock>
                    ) : (
                      <DirectDebitForm
                        setDirectDebitDataValid={setDirectDebitDataValid}
                        setDirectDebitEdit={setDirectDebitEdit}
                        useCounter={false}
                        monthlyFlex
                      />
                    )}
              </Col>
            </Row>
          </Card>
        )}
      </div>
      <div className={clsx('fs-6 text-secondary', isAppView ? 'm-3 p-3 bg-white border rounded' : 'p-4')}>
        {pageModel && (
          <PaymentHeader
            smallHeader="Payment 2"
            mainHeader="One-time card payment"
            description="Covers your first month"
            price={monthlyFlexCardPaymentPrice}
            combinedPaymentsCard
          />
        )}
        {!directDebitEdit && directDebitDataValid && (
        <Card className="border border-light shadow-sm bg-body rounded">
          <Row>
            <Col>
              {error?.message && error?.title ? (
                <InfoBlock header={error?.title} variant="danger" showIcon>
                  <span className="fw-bold text-danger">
                    {error?.message}
                  </span>
                </InfoBlock>
                  ) : (
                    <CardPaymentForm
                      header="Add card details"
                      error={error}
                    />
                  )}
            </Col>
          </Row>
        </Card>
          )}
      </div>
      <TimeoutWarningModal />
    </>
  );
}

export default CombinedPayments;
